import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap, ArrowRight, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { analytics } from '../hooks/useAnalytics';

const navLinks = [
  { label: "Platform", href: "/portal" },
  { label: "Consulting", href: "/consulting" },
  { label: "Mentors", href: "/mentors" },
  { label: "Results", href: "/results" },
  { label: "Deadlines", href: "/deadlines-grid" },
  { label: "Diagnostic", href: "/diagnostic" },
  { label: "Career Prep", href: "/career-portal" },
  { label: "Resources", href: "/resources" },
];

export function MarketingNav() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-xl border-b border-[#E2E2E2] z-50" data-testid="marketing-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <GraduationCap className="h-7 w-7 text-[#0F392B]" strokeWidth={1.5} />
          <span className="font-semibold text-xl tracking-tight whitespace-nowrap" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
        </Link>
        <div className="hidden xl:flex items-center gap-6">
          {navLinks.map(l => {
            const active = location.pathname === l.href || location.pathname.startsWith(l.href + '/');
            return (
              <Link key={l.href} to={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`} className={`text-sm whitespace-nowrap transition-colors ${active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                {l.label}
              </Link>
            );
          })}
        </div>
        <div className="hidden xl:flex items-center gap-3">
          <Link to="/sign-in"><Button variant="ghost" className="text-sm" data-testid="nav-sign-in">Sign In</Button></Link>
          <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('nav')}>
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-sm" data-testid="nav-portal-cta">Start the Platform — $99 <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </Link>
        </div>
        <button className="xl:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="xl:hidden bg-white border-b border-[#E2E2E2] py-4 px-4 space-y-2" data-testid="nav-mobile-menu">
          {navLinks.map((l) => (
            <Link key={l.href} to={l.href} onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-muted-foreground hover:text-foreground">{l.label}</Link>
          ))}
          <div className="pt-2 flex gap-2">
            <Link to="/sign-in"><Button variant="ghost" size="sm" className="text-xs">Sign In</Button></Link>
            <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('nav-mobile')}><Button size="sm" className="bg-[#0F392B] text-white text-xs">Start the Platform — $99</Button></Link>
          </div>
        </div>
      )}
    </nav>
  );
}

const footerLinks = [
  { heading: "The Firm", links: [
    { label: "The Admissions Portal", href: "/portal" },
    { label: "Consulting", href: "/consulting" },
    { label: "The Mentor Bench", href: "/mentors" },
    { label: "Results & Outcomes", href: "/results" },
    { label: "Ivy Signals Diagnostic", href: "/diagnostic" },
    { label: "Premium Advisory", href: "/premium" },
    { label: "The Case Method", href: "/case-method" },
    { label: "Why Us", href: "/compare" },
  ]},
  { heading: "Free Tools & Research", links: [
    { label: "The Playbook — Essays & Strategy", href: "/blog" },
    { label: "AnyZip Code", href: "/zip-code" },
    { label: "AnyPath Strategy", href: "/elite-strategy" },
    { label: "Career Prep", href: "/career-portal" },
    { label: "Free Guides & Resources", href: "/resources" },
    { label: "Common App Deadlines Grid", href: "/deadlines-grid" },
    { label: "No-Fee Colleges List", href: "/deadlines-grid/no-application-fee" },
    { label: "Test-Optional Colleges List", href: "/deadlines-grid/test-optional" },
    { label: "Facts & Data", href: "/facts" },
    { label: "Admissions Calculator", href: "/calculator" },
    { label: "School Profiles", href: "/schools" },
  ]},
  { heading: "Get Started", links: [
    { label: "Start the Platform", href: "/sign-up" },
    { label: "Book a Strategy Call", href: "/consultation" },
    { label: "Sign In", href: "/sign-in" },
  ]},
];

export function MarketingFooter() {
  return (
    <footer className="border-t border-[#E2E2E2] py-12 bg-white" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap className="h-6 w-6 text-[#0F392B]" strokeWidth={1.5} />
              <span className="font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              Our mission: bring Ivy League–level resources to the next generation. Every client is paired with a current Ivy League student mentor — mentors on staff across the country and around the world, on a mission to lift the students coming up behind them.
            </p>
            <p className="text-xs text-muted-foreground">24/7 On Call Staff — (202) 731-3100</p>
            <p className="text-xs text-muted-foreground">taimur@ivyleaguehelp.com</p>
            <p className="text-xs text-muted-foreground">333 Harvard St, Cambridge, MA 02139</p>
            <p className="text-xs text-muted-foreground">12941 Los Alamitos Ct, Orlando, FL 32837</p>
          </div>
          {footerLinks.map((group, i) => (
            <div key={i}>
              <p className="text-xs font-medium text-[#0F392B] uppercase tracking-wide mb-3">{group.heading}</p>
              <div className="space-y-2">
                {group.links.map((l, j) => (
                  <Link key={j} to={l.href} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">{l.label}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#E2E2E2] mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Ivy League Consulting. All rights reserved.</p>
          <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('footer')}>
            <Button size="sm" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8 px-4">
              Start the Platform — $99 <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </div>
    </footer>
  );
}
